import { createSlice } from "@reduxjs/toolkit"
import type { MissionTablePayload } from "@nova/schemas"
import { DateFilterValue } from "../filters/DateFilter"
import { DroneFilterValue } from "../filters/DroneFilter"
import { PilotFilterValue } from "../filters/PilotFilter"
import { TagFilterValue } from "../filters/TagFilter"

export const missionsTableSliceName = "pilot-tracking-missions-table" as const

export const ALL_MISSIONS_TABLE_FILTERS = [
    "date",
    "pilot",
    "drone",
    "tag",
    "checklist",
    "search",
] as const
export type MissionsTableFilter = (typeof ALL_MISSIONS_TABLE_FILTERS)[number]

export const ALL_MISSIONS_TABLE_COLUMNS = [
    "started_at",
    "pilot",
    "drone",
    "flightTime",
    "distanceFlown",
    "maxAltitude",
    "maxSpeed",
    "tags",
    "numChecklistSubmissions",
] as const
export type MissionsTableColumn = (typeof ALL_MISSIONS_TABLE_COLUMNS)[number]

export type SortField = NonNullable<NonNullable<MissionTablePayload["sort"]>["field"]>
export type SortDir = "ASC" | "DESC"
export type SortState = { field: SortField; dir: SortDir }

const initialState = {
    selectedMissionIds: {} as Record<string, boolean>,
    renamingGroupId: null as string | null,
    hoveredRowId: null as string | null,
    tagDropdownFlightId: null as string | null,
    openProjectDropdownFlightId: null as string | null,
    search: "",
    page: 1,
    checklistFilter: false,
    sortState: { field: "started_at", dir: "DESC" } as SortState,
    dateFilter: null as DateFilterValue | null,
    pilotFilter: null as PilotFilterValue | null,
    droneFilter: null as DroneFilterValue | null,
    tagFilter: [] as TagFilterValue,
}

export const {
    reducer: MissionsTableReducer,
    actions: {
        toggleMissionSelected,
        selectMissionRange,
        clearSelectedMissionIds,
        setMissionSelected,
        setRenamingGroupId,
        setHoveredRowId,
        setTagDropdownFlightId,
        setOpenProjectDropdownFlightId,
        setSearch,
        setPage,
        setChecklistFilter,
        setSortState,
        setDateFilter,
        setPilotFilter,
        setDroneFilter,
        setTagFilter,
    },
} = createSlice({
    name: missionsTableSliceName,
    initialState,
    reducers: {
        toggleMissionSelected: (state, { payload }: { payload: string }) => {
            state.selectedMissionIds[payload]
                ? delete state.selectedMissionIds[payload]
                : (state.selectedMissionIds[payload] = true)
        },
        selectMissionRange: (state, { payload }: { payload: string[] }) => {
            for (const id of payload) state.selectedMissionIds[id] = true
        },
        clearSelectedMissionIds: (state) => {
            state.selectedMissionIds = {}
        },
        setMissionSelected: (
            state,
            { payload }: { payload: { id: string; selected: boolean } }
        ) => {
            payload.selected
                ? (state.selectedMissionIds[payload.id] = payload.selected)
                : delete state.selectedMissionIds[payload.id]
        },
        setRenamingGroupId: (state, { payload }: { payload: string | null }) => {
            state.renamingGroupId = payload
        },
        setHoveredRowId: (state, { payload }: { payload: string | null }) => {
            state.hoveredRowId = payload
        },
        setTagDropdownFlightId: (state, { payload }: { payload: string | null }) => {
            state.tagDropdownFlightId = payload
        },
        setOpenProjectDropdownFlightId: (state, { payload }: { payload: string | null }) => {
            state.openProjectDropdownFlightId = payload
        },
        setSearch: (state, { payload }: { payload: string }) => {
            state.search = payload
            state.page = 1
        },
        setPage: (state, { payload }: { payload: number }) => {
            state.page = payload
        },
        setChecklistFilter: (state, { payload }: { payload: boolean }) => {
            state.checklistFilter = payload
            state.page = 1
        },
        setSortState: (state, { payload }: { payload: SortState }) => {
            state.sortState = payload
            state.page = 1
        },
        setDateFilter: (state, { payload }: { payload: DateFilterValue | null }) => {
            state.dateFilter = payload
            state.page = 1
        },
        setPilotFilter: (state, { payload }: { payload: PilotFilterValue | null }) => {
            state.pilotFilter = payload
            state.page = 1
        },
        setDroneFilter: (state, { payload }: { payload: DroneFilterValue | null }) => {
            state.droneFilter = payload
            state.page = 1
        },
        setTagFilter: (state, { payload }: { payload: TagFilterValue }) => {
            state.tagFilter = payload
            state.page = 1
        },
    },
})
